import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PlayCircleIcon } from '@heroicons/react/24/outline';

import { useAuth } from '../../hooks/use-auth';
import * as videosService from '../../services/api/videos';
import { strings } from '../../localization/strings';
import { fromJsDate, fromTimestamp } from '../../services/formatter/date';
import Heading1 from '../../components/common/headings/heading1';
import CommonAnimatedLoader from '../../components/common/animated/loader';
import AlertPrimary from '../../components/common/alerts/alert-primary';
import PaginationComponent from '../../components/common/pagination';
import FilterVideos from '../../entities/filter/videos';
import { fillFromParams } from '../../services/crud/filter';
import VideosFilter from './filter';

const initialState: FilterVideos = { page: 0, sort: '-created_at,-id', limit: 24 };

export default function VideosPageComponent() {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => {
    const restored = fillFromParams<FilterVideos>(initialState, searchParams, {
      isNumeric: ['page'],
      isDate: ['created_at_from', 'created_at_to'],
    });
    const filters: FilterVideos = {
      ...initialState,
      page: Number.isInteger(restored.page) && restored.page >= 0 ? restored.page : 0,
      search: restored.search || '',
    };

    for (const key of ['created_at_from', 'created_at_to'] as const) {
      const date = restored[key];

      if (date instanceof Date && !Number.isNaN(date.getTime())) filters[key] = date;
    }

    return filters;
  }, [searchParams]);
  const { data, error, isLoading } = useQuery({
    queryKey: ['videos', params],
    queryFn: () => videosService.list(token, params),
    refetchOnWindowFocus: false,
  });

  const updateParams = (filters: FilterVideos) => {
    const query: Record<string, string> = {};

    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null && value !== '') {
        query[key] = value instanceof Date ? fromJsDate(value) : String(value);
      }
    }
    setSearchParams(query);
  };

  return (
    <div>
      <header className="mb-3">
        <Heading1>{strings.navigation.video}</Heading1>
      </header>
      <VideosFilter
        initialState={initialState}
        filterState={params}
        onUpdate={(filters) => updateParams({ ...filters, page: 0 })}
      />
      {error instanceof Error && <AlertPrimary type="danger" text={error.message} />}
      {isLoading && <CommonAnimatedLoader />}
      {data && (
        <div className="py-5 lg:py-7">
          {data.items.length === 0 ? (
            <AlertPrimary text={strings.videos.empty} />
          ) : (
            <>
              <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 3xl:grid-cols-6">
                {data.items.map((video) => (
                  <Link
                    key={video.id}
                    to={`/videos/view/${video.id}`}
                    className="group overflow-hidden rounded-lg border border-gray-200 bg-white text-gray-900 no-underline hover:border-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
                  >
                    <div className="relative flex aspect-video items-center justify-center bg-gray-100">
                      {video.image_url && (
                        <img src={video.image_url} alt="" loading="lazy" className="h-full w-full object-cover" />
                      )}
                      <PlayCircleIcon
                        className="absolute h-12 w-12 rounded-full bg-black/40 text-white"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="space-y-2 p-3">
                      <div className="text-xs text-gray-500">
                        {video.created_at ? fromTimestamp(video.created_at, true) : strings.videos.unknownDate}
                      </div>
                      <h2 className="text-sm font-medium group-hover:text-primary">{video.title}</h2>
                      {!video.playlist_url && <div className="text-xs text-gray-500">{strings.videos.unavailable}</div>}
                    </div>
                  </Link>
                ))}
              </div>
              <PaginationComponent
                hasBg={false}
                pagination={data.pagination}
                onChangePage={(nextPage) => updateParams({ ...params, page: nextPage })}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}
